import { configureStore, createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import affiliateReducer from 'store/affiliateSlice';
import authReducer from 'store/authSlice';
import configReducer from 'store/configSlice';
import ecommerceSlice from 'store/ecommerceSlice';
import modalReducer from 'store/modalSlice';
import reportReducer from 'store/reportSlice';

import { adminAccessApi } from 'api/AdminAccessApi';
import { adminAccessMessagesApi } from 'api/AdminAccessMessagesApi';
import { artifactsApi } from 'api/ArtifactsApi';
import { authApi } from 'api/AuthApi';
import { commonApi } from 'api/CommonApi';
import { contactsApi } from 'api/ContactsApi';
import { designApi } from 'api/DesignApi';
import { flowAiApi } from 'api/FlowAiApi';
import { giftsApi } from 'api/GiftsApi';
import { hipaaApi } from 'api/HipaaApi';
import { inboxApi } from 'api/InboxApi';
import { lifeReviewApi } from 'api/LifeReviewApi';
import { messageAiApi } from 'api/MessageAiApi';
import { messagesApi } from 'api/MessagesApi';
import { organizationInvitesApi } from 'api/OrganizationInvitesApi';
import { paymentApi } from 'api/PaymentApi';
import { sysAdminApi } from 'api/SysAdminApi';
import { usersApi } from 'api/UsersApi';
import { logout } from 'store/authSlice';
import { runSecurityTeardown } from 'store/securityTeardown';

const securityTeardownListenerMiddleware = createListenerMiddleware();
let isSecurityTeardownRunning = false;

const isAuthPublicRoute = (pathname: string) => {
  return ['/login', '/signup', '/forgot-password', '/reset-password', '/confirm-email'].includes(pathname);
};

securityTeardownListenerMiddleware.startListening({
  matcher: isAnyOf(logout, authApi.endpoints.logout.matchFulfilled),
  effect: (_, listenerApi) => {
    if (isSecurityTeardownRunning) {
      return;
    }

    isSecurityTeardownRunning = true;
    try {
      if (typeof window !== 'undefined' && !isAuthPublicRoute(window.location.pathname)) {
        window.location.replace('/login');
      }

      runSecurityTeardown(listenerApi.dispatch);
    } finally {
      isSecurityTeardownRunning = false;
    }
  }
});

export const store = configureStore({
  reducer: {
    auth: authReducer,
    affiliate: affiliateReducer,
    report: reportReducer,
    modal: modalReducer,
    config: configReducer,
    ecommerce: ecommerceSlice,
    [authApi.reducerPath]: authApi.reducer,
    [designApi.reducerPath]: designApi.reducer,
    [commonApi.reducerPath]: commonApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [contactsApi.reducerPath]: contactsApi.reducer,
    [giftsApi.reducerPath]: giftsApi.reducer,
    [organizationInvitesApi.reducerPath]: organizationInvitesApi.reducer,
    [flowAiApi.reducerPath]: flowAiApi.reducer,
    [messageAiApi.reducerPath]: messageAiApi.reducer,
    [messagesApi.reducerPath]: messagesApi.reducer,
    [inboxApi.reducerPath]: inboxApi.reducer,
    [lifeReviewApi.reducerPath]: lifeReviewApi.reducer,
    [hipaaApi.reducerPath]: hipaaApi.reducer,
    [adminAccessApi.reducerPath]: adminAccessApi.reducer,
    [adminAccessMessagesApi.reducerPath]: adminAccessMessagesApi.reducer,
    [artifactsApi.reducerPath]: artifactsApi.reducer,
    [paymentApi.reducerPath]: paymentApi.reducer,
    [sysAdminApi.reducerPath]: sysAdminApi.reducer
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false })
      .prepend(securityTeardownListenerMiddleware.middleware)
      .concat(
        authApi.middleware,
        designApi.middleware,
        commonApi.middleware,
        usersApi.middleware,
        contactsApi.middleware,
        giftsApi.middleware,
        organizationInvitesApi.middleware,
        flowAiApi.middleware,
        messageAiApi.middleware,
        messagesApi.middleware,
        inboxApi.middleware,
        lifeReviewApi.middleware,
        hipaaApi.middleware,
        adminAccessApi.middleware,
        adminAccessMessagesApi.middleware,
        artifactsApi.middleware,
        paymentApi.middleware,
        sysAdminApi.middleware
      )
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;

export default store;
